import type { Holding, SalePlan, ValidatedHolding } from '@porttang/core';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/useAuth';
import { realizedQueryKey } from '../realized/useRealized';
import {
  applySale,
  createHolding,
  deleteHolding,
  listHoldings,
  newHoldingId,
  updateHolding,
} from './holdingsRepo';

export function holdingsQueryKey(uid: string) {
  return ['holdings', uid] as const;
}

/**
 * ใช้ uid ของผู้ใช้ปัจจุบัน — throw ถ้ายังไม่ login
 * hook พวกนี้ถูกเรียกจากหน้าที่อยู่หลัง RequireAuth เสมอ จึงไม่มีทางไม่มี user
 * แต่เขียนดักไว้ให้ TypeScript และให้พังชัด ๆ ถ้าวันหนึ่งมีคนเอาไปใช้นอก guard
 */
function useUid(): string {
  const { user } = useAuth();
  if (user === null) {
    throw new Error('ต้องเข้าสู่ระบบก่อนจึงจะใช้ข้อมูลสินทรัพย์ได้');
  }
  return user.uid;
}

export function useHoldings() {
  const uid = useUid();

  return useQuery({
    queryKey: holdingsQueryKey(uid),
    queryFn: () => listHoldings(uid),
  });
}

/**
 * ตัวช่วยสำหรับ optimistic update
 *
 * คืน list เดิมกลับมาเพื่อใช้ rollback ถ้าเขียนไม่สำเร็จ — ถ้าไม่ rollback
 * ผู้ใช้จะเห็นรายการที่บันทึกไม่ผ่านค้างอยู่บนหน้าจอราวกับว่าบันทึกสำเร็จแล้ว
 */
async function beginOptimistic(
  queryClient: QueryClient,
  uid: string,
  apply: (current: Holding[]) => Holding[],
): Promise<{ previous: Holding[] | undefined }> {
  const key = holdingsQueryKey(uid);
  // ยกเลิก refetch ที่ค้างอยู่ ไม่งั้นผลลัพธ์เก่าอาจกลับมาทับค่าที่เพิ่งแก้
  await queryClient.cancelQueries({ queryKey: key });

  const previous = queryClient.getQueryData<Holding[]>(key);
  queryClient.setQueryData<Holding[]>(key, (current) => apply(current ?? []));

  return { previous };
}

function rollback(
  queryClient: QueryClient,
  uid: string,
  context: { previous: Holding[] | undefined } | undefined,
): void {
  if (context?.previous !== undefined) {
    queryClient.setQueryData(holdingsQueryKey(uid), context.previous);
  }
}

export function useCreateHolding() {
  const uid = useUid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (holding: ValidatedHolding) => {
      const id = newHoldingId(uid);
      await createHolding(uid, id, holding);
      return id;
    },
    onMutate: async (holding) => {
      // id ตัวนี้ใช้แค่แสดงผลชั่วคราว ตัวจริงถูกสร้างใน mutationFn
      // ไม่ตรงกันก็ไม่เป็นไร เพราะ invalidate ตอนจบจะดึงของจริงมาแทนทั้งหมด
      const optimistic: Holding = { id: `optimistic-${Date.now()}`, ...holding };
      return beginOptimistic(queryClient, uid, (current) => [...current, optimistic]);
    },
    onError: (_error, _holding, context) => rollback(queryClient, uid, context),
    onSettled: () => queryClient.invalidateQueries({ queryKey: holdingsQueryKey(uid) }),
  });
}

export function useUpdateHolding() {
  const uid = useUid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, holding }: { id: string; holding: ValidatedHolding }) =>
      updateHolding(uid, id, holding),
    onMutate: ({ id, holding }) =>
      beginOptimistic(queryClient, uid, (current) =>
        current.map((item) => (item.id === id ? { id, ...holding } : item)),
      ),
    onError: (_error, _variables, context) => rollback(queryClient, uid, context),
    onSettled: () => queryClient.invalidateQueries({ queryKey: holdingsQueryKey(uid) }),
  });
}

export function useDeleteHolding() {
  const uid = useUid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteHolding(uid, id),
    onMutate: (id) =>
      beginOptimistic(queryClient, uid, (current) => current.filter((item) => item.id !== id)),
    onError: (_error, _id, context) => rollback(queryClient, uid, context),
    onSettled: () => queryClient.invalidateQueries({ queryKey: holdingsQueryKey(uid) }),
  });
}

/** ขาย — แก้ holding กับจดรายการขายพร้อมกัน แล้วโหลดใหม่ทั้งสองชุด */
export function useApplySale() {
  const uid = useUid();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ holdingId, plan }: { holdingId: string; plan: SalePlan }) =>
      applySale(uid, holdingId, plan),
    onMutate: ({ holdingId, plan }) => {
      // ดึงออกมาเป็นตัวแปรก่อน — TypeScript ไม่พา narrowing ของ property เข้าไปใน callback
      const next = plan.next;
      return beginOptimistic(queryClient, uid, (current) =>
        next === null
          ? current.filter((item) => item.id !== holdingId)
          : current.map((item) =>
              item.id === holdingId
                ? { ...item, quantity: next.quantity, avgCost: next.avgCost }
                : item,
            ),
      );
    },
    onError: (_error, _variables, context) => rollback(queryClient, uid, context),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: holdingsQueryKey(uid) }),
        queryClient.invalidateQueries({ queryKey: realizedQueryKey(uid) }),
      ]),
  });
}
